import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { IManagementGroup, IProfile, IResponse } from '@interfaces/index';
import { LibNotificationComponent } from '@ui-components/notification';
import { HttpService } from './http.service';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, Subject, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AppService {

  protected _profile: IProfile | null = null;
  public loading = true;
  public on_scroll: Subject<number> = new Subject();

  private _selectedManagementGroupSubject: BehaviorSubject<IManagementGroup | null> =
    new BehaviorSubject<IManagementGroup | null>(null);
  public selectedManagementGroup$: Observable<IManagementGroup | null> =
    this._selectedManagementGroupSubject.asObservable();

  constructor(
    private _http_service: HttpService,
    private _snack_bar: MatSnackBar,
    private _cookie_service: CookieService,
    private _router: Router,
  ) { }

  getProfile(): Observable<IResponse<IProfile>> {
    if (this._profile) {
      return of({
        data: this._profile,
        code: 200
      });
    }

    return this._http_service.post<IProfile>('auth/profile', {}).pipe(
      map((response: IResponse<IProfile>) => {
        this._profile = response.data;
        return response;
      }),
      catchError((error) => {
        console.error('Profile fetching error:', error);
        this.notify('Failed to fetch profile. Please try again later.', 'error');
        return throwError(() => new Error('Profile fetching failed.'));
      })
    );
  }

  setSelectedManagementGroup(group: IManagementGroup): void {
    this._cookie_service.set('selected_management_group_org_id', String(group.id), 30, '/', environment.domain, location.protocol === 'https:', 'Lax');
    this._selectedManagementGroupSubject.next(group);
  }

  getSelectedManagementGroupFromCookies(managementGroups: IManagementGroup[]): void {
    const selectedOrgId = this._cookie_service.get('selected_management_group_org_id');

    if (selectedOrgId) {
      const selectedGroup = this.getManagementGroupById(Number(selectedOrgId), managementGroups);

      if (selectedGroup) {
        this._selectedManagementGroupSubject.next(selectedGroup);
      } else {
        console.error('No management group found for org_id:', selectedOrgId);
        this._selectedManagementGroupSubject.next(null);
      }
    } else {
      console.warn('No selected org_id found in cookies');
      this._selectedManagementGroupSubject.next(null);
    }
  }

  clearSelectedManagementGroup(): void {
    this._cookie_service.delete('selected_management_group_org_id', '/', environment.domain);
    this._selectedManagementGroupSubject.next(null);
  }

  notify(message: string, status: string) {
    // Success and info are transient at 4s, warning holds longer at 8s, and an
    // error persists until the person dismisses it — an error is not allowed
    // to vanish before it is read. Omitting `duration` for an error leaves
    // the snack bar open until the notification's own dismiss control (or a
    // programmatic dismiss) closes it.
    const duration = status === 'error' ? undefined
      : status === 'warning' ? 8000
      : 4000;

    this._snack_bar.openFromComponent(LibNotificationComponent, {
      verticalPosition: 'bottom',
      horizontalPosition: 'right',
      panelClass: 'lib-notification',
      data: {
        message,
        status
      },
      ...(duration !== undefined ? { duration } : {})
    });
  }

  private getManagementGroupById(id: number, managementGroups: IManagementGroup[]): IManagementGroup | null {
    const foundGroup = managementGroups.find(group => group.id === id);

    if (foundGroup) {
      return foundGroup;
    } else {
      console.error('No management group found for ID:', id);
      return null;
    }
  }
}
